/**
 * Annual target grid (القيم المستهدفة سنوياً) — inline editable grid for the
 * KPI form. Grouped by measurement year; the row layout depends on the pattern:
 *
 *  - fixed:   one editable base "القيمة المستهدفة للقراءة" + computed year total.
 *  - custom:  N editable period cells (الربع 1-4 / months) + computed year total.
 *  - inc/dec: one editable base + computed year total, then N READ-ONLY period
 *             cells auto-filled from the base (compounding/stepping within the
 *             year, resetting each year).
 *
 * The editable values live in `inputs` keyed "year:period_index"; for non-custom
 * patterns only "year:1" (the per-year base) is used. Parent computes the API
 * `target_values[]` from the same `inputs` via computeGrid at submit time.
 */
import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TextInput } from 'react-native';
import { useTranslation } from 'react-i18next';
import { tokens } from '@design';
import { FONTS } from '../../../constants/config';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getTextAlign, normalizeArabicNumbers } from '../../../utils/rtl';
import type { Frequency, TargetPattern, TargetAggregation } from '../../../types';
import {
  computeGrid,
  groupByYear,
  yearlyTotal,
  overallTotal,
  GRIDLESS_FREQUENCIES,
  type GridCell,
} from '../../../utils/targetGrid';

interface AnnualTargetGridProps {
  frequency: Frequency | '' | string;
  pattern: TargetPattern;
  aggregation: TargetAggregation;
  unit: string;
  start: string | null;
  end: string | null;
  stepPercent: string;
  stepAmount: string;
  inputs: Record<string, number>;
  onInputChange: (key: string, value: number) => void;
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

export default function AnnualTargetGrid({
  frequency,
  pattern,
  aggregation,
  unit,
  start,
  end,
  stepPercent,
  stepAmount,
  inputs,
  onInputChange,
}: AnnualTargetGridProps) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL, colors), [isRTL, colors]);

  const cells = useMemo<GridCell[]>(() => {
    if (!frequency || GRIDLESS_FREQUENCIES.has(frequency as Frequency)) return [];
    return computeGrid({
      frequency: frequency as Frequency,
      pattern,
      unit,
      start,
      end,
      stepPercent: parseFloat(stepPercent) || 0,
      stepAmount: parseFloat(stepAmount) || 0,
      inputs,
    });
  }, [frequency, pattern, unit, start, end, stepPercent, stepAmount, inputs]);

  if (frequency && GRIDLESS_FREQUENCIES.has(frequency as Frequency)) {
    return (
      <View style={styles.notice}>
        <Text style={styles.noticeText}>{t('kpis.gridDailyWeeklyNotice')}</Text>
      </View>
    );
  }
  if (!frequency || !start || !end) {
    return (
      <View style={styles.notice}>
        <Text style={styles.noticeText}>{t('kpis.gridNeedsDatesNotice')}</Text>
      </View>
    );
  }
  if (cells.length === 0) {
    return (
      <View style={styles.notice}>
        <Text style={styles.noticeText}>{t('kpis.gridNoPeriodsNotice')}</Text>
      </View>
    );
  }

  const byYear = groupByYear(cells);
  const years = Array.from(byYear.keys()).sort((a, b) => a - b);
  const grandTotal = overallTotal(cells.map((c) => c.value), unit, aggregation);
  const isCustom = pattern === 'custom';
  const isFixed = pattern === 'fixed';
  // Yearly = 1 period/year → no 2-column pairing; stack cells full-width so the
  // input and the totals align in a single column instead of diagonally.
  const periodsPerYear = years.reduce(
    (max, y) => Math.max(max, (byYear.get(y) || []).length),
    1,
  );
  const singleCol = periodsPerYear <= 1;

  // Render a pair of cells in a 2-column row.
  const renderCellPair = (
    pair: GridCell[],
    year: number,
    editable: boolean,
  ) => (
    <View style={styles.cellRow} key={pair.map((c) => c.period_index).join('-')}>
      {pair.map((cell) => {
        const key = `${cell.year}:${cell.period_index}`;
        return (
          <View style={styles.cellBox} key={key}>
            <View style={styles.labelRow}>
              {editable && <Text style={styles.star}>*</Text>}
              <Text style={styles.cellLabel}>{cell.period_label}</Text>
            </View>
            {editable ? (
              <TextInput
                style={styles.input}
                value={inputs[key] != null ? String(inputs[key]) : ''}
                onChangeText={(v) => {
                  const num = parseFloat(normalizeArabicNumbers(v));
                  onInputChange(key, Number.isNaN(num) ? 0 : num);
                }}
                keyboardType="numeric"
                textAlign={isRTL ? 'right' : 'left'}
                placeholder="0.0"
                placeholderTextColor={tokens.colors.raw.gray['400']}
              />
            ) : (
              <View style={styles.computedBox}>
                <Text style={styles.computedText}>{fmt(cell.value)}</Text>
              </View>
            )}
          </View>
        );
      })}
      {pair.length === 1 && !singleCol && <View style={styles.cellBox} />}
    </View>
  );

  // The editable base row ("القيمة المستهدفة للقراءة") + computed year total.
  const renderBaseRow = (year: number, yearTotal: number) => {
    const key = `${year}:1`;
    return (
      <View style={styles.cellRow}>
        <View style={styles.cellBox}>
          <View style={styles.labelRow}>
            <Text style={styles.star}>*</Text>
            <Text style={styles.cellLabel}>{t('kpis.firstTargetValue')}</Text>
          </View>
          <TextInput
            style={styles.input}
            value={inputs[key] != null ? String(inputs[key]) : ''}
            onChangeText={(v) => {
              const num = parseFloat(normalizeArabicNumbers(v));
              onInputChange(key, Number.isNaN(num) ? 0 : num);
            }}
            keyboardType="numeric"
            textAlign={isRTL ? 'right' : 'left'}
            placeholder="0.0"
            placeholderTextColor={tokens.colors.raw.gray['400']}
          />
        </View>
        <View style={styles.cellBox}>
          <View style={styles.labelRow}>
            <Text style={styles.cellLabel}>{t('kpis.yearTarget')}</Text>
          </View>
          <View style={styles.computedBox}>
            <Text style={styles.computedText}>{fmt(yearTotal)}</Text>
          </View>
        </View>
      </View>
    );
  };

  const pairs = (arr: GridCell[]): GridCell[][] => {
    const out: GridCell[][] = [];
    for (let i = 0; i < arr.length; i += 2) out.push(arr.slice(i, i + 2));
    return out;
  };

  return (
    <View>
      {years.map((year) => {
        const yearCells = byYear.get(year) || [];
        const yearTotal = yearlyTotal(yearCells.map((c) => c.value), unit, aggregation);
        return (
          <View key={year} style={styles.yearRow}>
            <View style={styles.yearBadge}>
              <Text style={styles.yearBadgeText}>{year}</Text>
            </View>
            <View style={styles.fieldsCol}>
              {isCustom ? (
                <>
                  {pairs(yearCells).map((pair) => renderCellPair(pair, year, true))}
                  <View style={styles.cellRow}>
                    {!singleCol && <View style={styles.cellBox} />}
                    <View style={styles.cellBox}>
                      <View style={styles.labelRow}>
                        <Text style={styles.cellLabel}>{t('kpis.yearTarget')}</Text>
                      </View>
                      <View style={styles.computedBox}>
                        <Text style={styles.computedText}>{fmt(yearTotal)}</Text>
                      </View>
                    </View>
                  </View>
                </>
              ) : (
                <>
                  {renderBaseRow(year, yearTotal)}
                  {!isFixed && pairs(yearCells).map((pair) => renderCellPair(pair, year, false))}
                </>
              )}
            </View>
          </View>
        );
      })}

      {/* Overall total */}
      <View style={styles.yearRow}>
        <View style={styles.yearBadge} />
        <View style={styles.fieldsCol}>
          <View style={styles.cellRow}>
            {!singleCol && <View style={styles.cellBox} />}
            <View style={styles.cellBox}>
              <View style={styles.labelRow}>
                <Text style={styles.cellLabel}>{t('kpis.overallTarget')}</Text>
              </View>
              <View style={[styles.computedBox, styles.grandBox]}>
                <Text style={styles.computedText}>{fmt(grandTotal)}</Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

const getStyles = (isRTL: boolean, colors: any) =>
  StyleSheet.create({
    notice: {
      backgroundColor: colors.backgroundAlt,
      borderRadius: tokens.radii.absolute['radius-8'],
      padding: tokens.spacing.absolute['4'],
    },
    noticeText: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.regular,
      color: colors.textMuted,
      textAlign: getTextAlign(isRTL),
    },
    yearRow: {
      flexDirection: isRTL ? 'row-reverse' : 'row',
      gap: tokens.spacing.absolute['3'],
      marginBottom: tokens.spacing.absolute['3'],
      alignItems: 'stretch',
    },
    yearBadge: {
      width: 56,
      backgroundColor: colors.backgroundAlt,
      borderRadius: tokens.radii.absolute['radius-8'],
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: tokens.spacing.absolute['2'],
    },
    yearBadgeText: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.medium,
      color: colors.textPrimary,
    },
    fieldsCol: {
      flex: 1,
      gap: tokens.spacing.absolute['2'],
    },
    cellRow: {
      flexDirection: isRTL ? 'row-reverse' : 'row',
      gap: tokens.spacing.absolute['3'],
    },
    cellBox: {
      flex: 1,
    },
    labelRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: tokens.spacing.absolute['1'],
      marginBottom: tokens.spacing.absolute['1'],
    },
    star: {
      fontSize: tokens.typography.paragraph['paragraph mini font size'],
      color: tokens.colors.raw.red['500'],
    },
    cellLabel: {
      fontSize: tokens.typography.paragraph['paragraph mini font size'],
      fontFamily: FONTS.regular,
      color: colors.textMuted,
      textAlign: getTextAlign(isRTL),
    },
    input: {
      backgroundColor: colors.card,
      borderWidth: tokens.spacing.absolute['px'],
      borderColor: colors.border,
      borderRadius: tokens.radii.absolute['radius-8'],
      paddingHorizontal: tokens.spacing.absolute['3'],
      paddingVertical: tokens.spacing.absolute['2'],
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.regular,
      color: colors.textPrimary,
      minHeight: 40,
    },
    computedBox: {
      backgroundColor: colors.backgroundAlt,
      borderWidth: tokens.spacing.absolute['px'],
      borderColor: colors.border,
      borderRadius: tokens.radii.absolute['radius-8'],
      paddingHorizontal: tokens.spacing.absolute['3'],
      paddingVertical: tokens.spacing.absolute['2'],
      minHeight: 40,
      justifyContent: 'center',
    },
    grandBox: {
      backgroundColor: colors.backgroundAlt,
    },
    computedText: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.medium,
      color: colors.textMuted,
      textAlign: isRTL ? 'left' : 'right',
    },
  });
